// src/scenes/purchases/CreatePurchase.jsx
import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import {
  Snackbar,
  Alert,
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { Formik, FieldArray } from "formik";
import Autocomplete from "@mui/material/Autocomplete";

const token = localStorage.getItem("token");
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

const createSupplier = (name) =>
  api.post("/api/customer", {
    name,
    tinNumber: "0000000000",
    type: "supplier",
    phone: "0000000000",
    address: "Addis Ababa, Ethiopia",
  });

const createWarehouse = (name) =>
  api.post("/api/warehouse", {
    name,
  });

/* ---------- API ---------- */
const getSuppliers = () =>
  api
    .get("/api/customer", { params: { type: "supplier", _t: Date.now() } })
    .then((r) => r.data?.data.filter((c) => c?.type === "supplier"));
const getItems = () =>
  api.get("/api/item", { params: { _t: Date.now() } }).then((r) => r.data?.data);
const getWarehouses = () =>
  api.get("/api/warehouse", { params: { _t: Date.now() } }).then((r) => r.data?.data);

const postPurchase = (body) =>
  api
    .post("/api/purchase", body)
    .then((r) => ({ data: r.data }))
    .catch((e) => ({ error: e.response?.data?.message || e.message }));

/* ---------- Helpers ---------- */


/* ---------- Validation ---------- */
const SCHEMA = yup.object({
supplierId: yup.mixed().required("Supplier is required"),
warehouseId: yup.mixed().required("Warehouse is required"),

  fsNo: yup.string().required("FS No is required"),

  description: yup.string(),

  items: yup
    .array()
    .of(
      yup.object({
        itemId: yup.number().required("Select item"),

        quantity: yup.number().positive().required("Quantity is required"),

        unitPrice: yup.number().min(0).required("Unit price is required"),
      }),
    )
    .min(1, "Add at least one item"),
});

/* ---------- MAIN PAGE ---------- */
export default function CreatePurchase() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const load = async () => {
    setLoading(true);
    try {
      const [s, i, w, c] = await Promise.all([
        getSuppliers(),
        getItems(),
        getWarehouses(),
        
      ]);
      if (!mounted.current) return;
      setSuppliers(Array.isArray(s) ? s : []);
      setItems(Array.isArray(i) ? i : []);
      setWarehouses(Array.isArray(w) ? w : []);
      setCars(Array.isArray(c) ? c : []);
    } catch {
      setToast({ open: true, msg: "Failed to load data", sev: "error" });
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    load();
    const id = setInterval(load, 30000);
    const focus = () => load();
    window.addEventListener("focus", focus);
    return () => {
      mounted.current = false;
      clearInterval(id);
      window.removeEventListener("focus", focus);
    };
  }, []);

  /* ---------- Calculations ---------- */
  const calcTotals = (values) => {
    const subTotal = values.items.reduce(
      (sum, i) => sum + (Number(i.quantity) || 0) * (Number(i.unitPrice) || 0),
      0,
    );

    const exciseAmt = values.items.reduce((sum, i) => {
      const itemObj = items.find((x) => x.id === Number(i.itemId));
      const exciseFromMaster =
        ((itemObj?.ExciseTax || 0) / 100) *
        (Number(i.quantity) * Number(i.unitPrice));
      const exciseFromField = Number(i.exciseTax) || 0; // user input
      return sum + exciseFromMaster + exciseFromField;
    }, 0);
    const afterExcise = subTotal + exciseAmt;

    const disc1 = Number(values.priceDiscount || 0);
    const disc2 = Number(values.specialSalesDiscount || 0);
    const charge = Number(values.serviceCharge || 0);
    const fixedDisc = Number(values.fixedPriceDiscount || 0);

    const beforeVat = afterExcise - disc1 - disc2 + charge;
    const vat = beforeVat * 0.15;
    const withVat = beforeVat * 1.15;
    const afterDisc = withVat - fixedDisc;

    let withhold;

    if (values.withholdingType === "beer") {
      // SAME AS OLD PAGE (beer logic)
      withhold = (subTotal + charge - (disc1 + disc2 + fixedDisc)) * 0.03;
    } else {
      // SAME AS OLD PAGE (soft drink logic)
      withhold = subTotal * 0.03;
    }

    return {
      subTotal,
      exciseAmt,
      afterExcise,
      beforeVat,
      vat,
      withVat,
      afterDisc,
      withhold,
    };
  };

  const INITIALS = {
    supplierId: "",
    warehouseId: "",
    fsNo: "",
    description: "",
    purchaseDate: new Date().toISOString().split("T")[0],

    items: [
      {
        itemId: "",
        quantity: "",
        unitPrice: "",
      },
    ],
  };

  /* ---------- Submit ---------- */
  const submit = async (values, { setSubmitting, resetForm }) => {
  try {
    let supplierId = values.supplierId;
    let warehouseId = values.warehouseId;

    // Create supplier if user typed a new one
    if (typeof supplierId === "string") {
      const supplierRes = await createSupplier(
        supplierId.trim()
      );

      supplierId = supplierRes.data.data.id;
    }

    // Create warehouse if user typed a new one
    if (typeof warehouseId === "string") {
      const warehouseRes = await createWarehouse(
        warehouseId.trim()
      );

      warehouseId = warehouseRes.data.data.id;
    }

    const payload = {
      supplierId: Number(supplierId),
      warehouseId: Number(warehouseId),

      purchaseDate: values.purchaseDate,
      fsNo: values.fsNo || null,
      description: values.description || null,

      items: values.items.map((i) => ({
        itemId: Number(i.itemId),
        quantity: Number(i.quantity),
        unitPrice: Number(i.unitPrice),
      })),
    };

    const { data, error } = await postPurchase(payload);

    setSubmitting(false);

    if (error) {
      return setToast({
        open: true,
        msg: `Error: ${error}`,
        sev: "error",
      });
    }

    // reload dropdown data
    await load();

    setToast({
      open: true,
      msg: "Purchase saved successfully!",
      sev: "success",
    });

    resetForm();
  } catch (err) {
    setSubmitting(false);

    setToast({
      open: true,
      msg:
        err.response?.data?.message ||
        err.message ||
        "Failed to save purchase",
      sev: "error",
    });
  }
};

  return (
    <>
      <Box m="20px">
        <Header
          title="CREATE PURCHASE"
          subtitle="All calculations update live"
        />

        <Formik
          initialValues={INITIALS}
          validationSchema={SCHEMA}
          onSubmit={submit}
        >
          {(bag) => {
            const totals = calcTotals(bag.values);

            return (
              <form onSubmit={bag.handleSubmit}>
                <Box
                  display="grid"
                  gap="25px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": {
                      gridColumn: isNonMobile ? undefined : "span 4 !important",
                    },
                  }}
                >
                  {/* Supplier / Warehouse / Car */}
                 <Autocomplete
  freeSolo
  options={suppliers}
  getOptionLabel={(option) =>
    typeof option === "string" ? option : option.name
  }
  value={
    suppliers.find((s) => s.id === bag.values.supplierId) ||
    bag.values.supplierId ||
    null
  }
  onChange={(event, newValue) => {
    if (typeof newValue === "string") {
      bag.setFieldValue("supplierId", newValue);
    } else if (newValue) {
      bag.setFieldValue("supplierId", newValue.id);
    } else {
      bag.setFieldValue("supplierId", "");
    }
  }}
  onInputChange={(event, newInputValue) => {
    if (typeof bag.values.supplierId !== "number") {
      bag.setFieldValue("supplierId", newInputValue);
    }
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Supplier"
      variant="filled"
      error={
        bag.touched.supplierId &&
        Boolean(bag.errors.supplierId)
      }
      helperText={
        bag.touched.supplierId &&
        bag.errors.supplierId
      }
    />
  )}
/>

                 <Autocomplete
  freeSolo
  options={warehouses}
  getOptionLabel={(option) =>
    typeof option === "string" ? option : option.name
  }
  value={
    warehouses.find((w) => w.id === bag.values.warehouseId) ||
    bag.values.warehouseId ||
    null
  }
  onChange={(event, newValue) => {
    if (typeof newValue === "string") {
      bag.setFieldValue("warehouseId", newValue);
    } else if (newValue) {
      bag.setFieldValue("warehouseId", newValue.id);
    } else {
      bag.setFieldValue("warehouseId", "");
    }
  }}
  onInputChange={(event, newInputValue) => {
    if (typeof bag.values.warehouseId !== "number") {
      bag.setFieldValue("warehouseId", newInputValue);
    }
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Warehouse"
      variant="filled"
      error={
        bag.touched.warehouseId &&
        Boolean(bag.errors.warehouseId)
      }
      helperText={
        bag.touched.warehouseId &&
        bag.errors.warehouseId
      }
    />
  )}
/>

                    <TextField
                    label="FS No"
                    name="fsNo"
                    value={bag.values.fsNo}
                    onChange={bag.handleChange}
                    onBlur={bag.handleBlur}
                    variant="filled"
                    fullWidth
                    error={bag.touched.fsNo && Boolean(bag.errors.fsNo)}
                    helperText={bag.touched.fsNo && bag.errors.fsNo}
                  />

                  <TextField
                    label="Purchase Date"
                    type="date"
                    name="purchaseDate"
                    value={bag.values.purchaseDate}
                    onChange={bag.handleChange}
                    variant="filled"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />

                  {/* Dynamic Items */}
                  <FieldArray name="items">
                    {({ push, remove }) => (
                      <Box sx={{ gridColumn: "span 2" }}>
                        {bag.values.items.map((item, index) => {
                          const itemObj = items.find(
                            (x) => x.id === Number(item.itemId),
                          );
                          const qty = Number(item.quantity) || 0;
                          const price = Number(item.unitPrice) || 0;
                          const total = qty * price;

                          return (
                            <Box
                              key={index}
                              display="grid"
                              gap="15px"
                              mt={2}
                              mb={1}
                              p={2}
                              borderRadius={2}
                              border="1px solid #4444"
                              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                              sx={{
                                "& > div": {
                                  gridColumn: isNonMobile
                                    ? undefined
                                    : "span 4 !important",
                                },
                              }}
                            >
                              <TextField
                                select
                                label="Item"
                                name={`items[${index}].itemId`}
                                value={item.itemId}
                                onChange={bag.handleChange}
                                onBlur={bag.handleBlur}
                                variant="filled"
                                error={
                                  bag.touched.items?.[index]?.itemId &&
                                  Boolean(bag.errors.items?.[index]?.itemId)
                                }
                                helperText={
                                  bag.touched.items?.[index]?.itemId &&
                                  bag.errors.items?.[index]?.itemId
                                }
                              >
                                {items.map((i) => (
                                  <MenuItem key={i.id} value={i.id}>
                                    {i.name}
                                  </MenuItem>
                                ))}
                              </TextField>

                              <TextField
                                type="number"
                                label="Quantity"
                                name={`items[${index}].quantity`}
                                min="0"
                                value={item.quantity}
                                onChange={bag.handleChange}
                                // onBlur={bag.handleBlur}
                                onWheel={(e) => e.target.blur()}
                                variant="filled"
                                error={
                                  bag.touched.items?.[index]?.quantity &&
                                  Boolean(bag.errors.items?.[index]?.quantity)
                                }
                                helperText={
                                  bag.touched.items?.[index]?.quantity &&
                                  bag.errors.items?.[index]?.quantity
                                }
                              />

                              <TextField
                                type="number"
                                label="Unit Price"
                                name={`items[${index}].unitPrice`}
                                value={item.unitPrice}
                                onChange={bag.handleChange}
                                variant="filled"
                              />

                              {/* Show Excise only for Draught */}
                              {item.itemId &&
                                itemObj?.name
                                  .toLowerCase()
                                  .includes("drought") && (
                                  <TextField
                                    type="number"
                                    label="Excise Tax"
                                    name={`items[${index}].exciseTax`}
                                    value={item.exciseTax || ""}
                                    onChange={bag.handleChange}
                                    variant="filled"
                                    inputProps={{ min: 0, step: 0.01 }}
                                  />
                                )}

                              <Button
                                type="button"
                                color="error"
                                onClick={() => remove(index)}
                              >
                                Remove
                              </Button>
                            </Box>
                          );
                        })}

                        <Button
                          type="button"
                          variant="outlined"
                          onClick={() =>
                            push({
                              itemId: "",
                              quantity: "",
                              unitPrice: "",
                            })
                          }
                        >
                          + Add Item
                        </Button>
                      </Box>
                    )}
                  </FieldArray>

                

                  <TextField
                    label="Description"
                    name="description"
                    value={bag.values.description}
                    onChange={bag.handleChange}
                    variant="filled"
                    fullWidth
                    multiline
                    rows={3}
                  />

                  {/* Discounts & Charges */}
                  {/* <TextField
                    type="number"
                    label="Price Discount"
                    name="priceDiscount"
                    value={bag.values.priceDiscount}
                    onChange={bag.handleChange}
                    variant="filled"
                  />
                  <TextField
                    type="number"
                    label="Special Discount"
                    name="specialSalesDiscount"
                    value={bag.values.specialSalesDiscount}
                    onChange={bag.handleChange}
                    variant="filled"
                  />
                  <TextField
                    type="number"
                    label="Service Charge"
                    name="serviceCharge"
                    value={bag.values.serviceCharge}
                    onChange={bag.handleChange}
                    variant="filled"
                  />
                  <TextField
                    type="number"
                    label="Fixed Discount"
                    name="fixedPriceDiscount"
                    value={bag.values.fixedPriceDiscount}
                    onChange={bag.handleChange}
                    variant="filled"
                  />

                  <TextField
                    select
                    label="Withholding Type"
                    name="withholdingType"
                    value={bag.values.withholdingType}
                    onChange={bag.handleChange}
                    variant="filled"
                    fullWidth
                  >
                    <MenuItem value="beer">Beer / Draught</MenuItem>
                    <MenuItem value="soft_drink">
                      Soft Drink / Sun Chips
                    </MenuItem>
                  </TextField>

                 
                  <TextField
                    label="Subtotal"
                    value={totals.subTotal.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />

                  {/* <TextField
                    label="After Excise"
                    value={totals.afterExcise.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  /> 
                  <TextField
                    label="Before VAT"
                    value={totals.beforeVat.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />
                  <TextField
                    label="VAT"
                    value={totals.vat.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />
                  <TextField
                    label="Total with VAT"
                    value={totals.withVat.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />
                  <TextField
                    label="After Discount"
                    value={totals.afterDisc.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />
                  <TextField
                    label="Withholding (3%)"
                    value={totals.withhold.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                  />*/}
                </Box> 

                {/* Buttons */}
                <Box display="flex" justifyContent="end" mt="20px" gap={2}>
                  <Button onClick={() => bag.resetForm()} variant="outlined">
                    Reset
                  </Button>
                  <Box position="relative" display="inline-flex">
                    <Button
                      type="submit"
                      color="secondary"
                      variant="contained"
                      disabled={bag.isSubmitting} // disable while loading
                    >
                      Save Purchase
                    </Button>

                    {bag.isSubmitting && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: "primary.main",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          marginTop: "-12px", // half of size
                          marginLeft: "-12px",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={toast.sev}
          variant="filled"
          onClose={() => setToast((s) => ({ ...s, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
